// Authentication context provider
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, User, LoginRequest, RegisterRequest, APIError } from '../types';
import { apiService } from '../services/api';

// Actions
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: User }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'LOAD_USER_START' }
  | { type: 'LOAD_USER_SUCCESS'; payload: User }
  | { type: 'LOAD_USER_FAILURE'; payload: string }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading to check existing auth
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'LOAD_USER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'REGISTER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
      };

    case 'LOAD_USER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
    case 'LOAD_USER_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Context type
interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  loadUser: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load current user
  const loadUser = async (): Promise<void> => {
    try {
      dispatch({ type: 'LOAD_USER_START' });

      const user = await apiService.getCurrentUser();
      console.log('✅ User loaded successfully:', user);
      console.log('🔍 User role:', user.role);
      console.log('🔍 Is admin?:', user.role === 'admin');
      dispatch({ type: 'LOAD_USER_SUCCESS', payload: user });
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to load user';
      console.error('❌ Failed to load user:', errorMessage);
      dispatch({ type: 'LOAD_USER_FAILURE', payload: errorMessage });
      
      // Clear invalid token
      localStorage.removeItem('access_token');
      apiService.logout();
    }
  };

  // Load user on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token && !state.user) {
      loadUser().catch(() => {
        // Error already handled in loadUser
      });
    } else if (!token) {
      // No token is normal when not logged in - just set loading to false
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  // Login function
  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const authResponse = await apiService.login(credentials);
      const user = await apiService.getCurrentUser();

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          token: authResponse.access_token,
        },
      });

      console.log('✅ User logged in successfully');
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Login failed';
      console.error('❌ Login failed:', errorMessage);
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Register function
  const register = async (userData: RegisterRequest): Promise<void> => {
    try {
      dispatch({ type: 'REGISTER_START' });

      await apiService.register(userData);

      dispatch({ type: 'REGISTER_SUCCESS', payload: {} as User });
      console.log('✅ User registered successfully');
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Registration failed';
      console.error('❌ Registration failed:', errorMessage);
      dispatch({ type: 'REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Logout function
  const logout = (): void => {
    console.log('🔓 Logging out user');
    apiService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Context value
  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    loadUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
